import { useState } from "react";
import { Mail, Phone, CalendarDays, Truck, Save } from "lucide-react";
import { toast } from "sonner";
import { updateOrder, apiError, STATUSES } from "@/lib/admin";

const fmt = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

export default function AdminOrderCard({ order, onChange }) {
    const [note, setNote] = useState(order.admin_note || "");
    const [saving, setSaving] = useState(false);
    const status = STATUSES.find((s) => s.value === order.status) || STATUSES[0];

    const save = async (changes) => {
        setSaving(true);
        try {
            onChange(await updateOrder(order.id, changes));
            toast.success("Saved");
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <article className="rounded-[1.5rem] bg-white border border-[#F2E1E6] p-6 shadow-[0_12px_30px_rgba(61,35,24,0.06)]" data-testid={`admin-order-${order.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h3 className="font-display text-xl font-bold text-[#3D2318]" data-testid="admin-order-name">{order.name}</h3>
                    <p className="text-xs text-[#8C6A5D]">Requested {fmt(order.created_at)}</p>
                </div>
                <select
                    value={order.status}
                    disabled={saving}
                    onChange={(e) => save({ status: e.target.value })}
                    data-testid="admin-order-status"
                    className={`rounded-full border px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.15em] outline-none ${status.className}`}
                >
                    {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
            </div>

            <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-[#6B4A3E]">
                <a href={`mailto:${order.email}`} className="flex items-center gap-2 hover:text-[#C95B7D]" data-testid="admin-order-email"><Mail className="h-4 w-4 text-[#C59B27]" />{order.email}</a>
                {order.phone && <a href={`tel:${order.phone}`} className="flex items-center gap-2 hover:text-[#C95B7D]" data-testid="admin-order-phone"><Phone className="h-4 w-4 text-[#C59B27]" />{order.phone}</a>}
                <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#C59B27]" />{order.event_date || "No date given"}</p>
                <p className="flex items-center gap-2"><Truck className="h-4 w-4 text-[#C59B27]" />{order.service_type}</p>
            </div>

            <div className="mt-4 rounded-2xl bg-[#FDF0F4]/60 p-4 text-sm text-[#3D2318]">
                <p className="font-semibold">{order.category || "—"}{order.quantity ? ` · ${order.quantity}` : ""}</p>
                {order.flavor && <p className="mt-1 text-[#C95B7D] font-medium" data-testid="admin-order-flavor">Flavor: {order.flavor}</p>}
                {order.notes && <p className="mt-1 whitespace-pre-line text-[#6B4A3E]">{order.notes}</p>}
            </div>

            <div className="mt-4">
                <label className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C59B27]">Private note</label>
                <div className="mt-1.5 flex gap-2">
                    <textarea
                        rows={2}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Agreed price, pickup time, flavor details…"
                        data-testid="admin-order-note"
                        className="w-full rounded-2xl border border-[#F2E1E6] bg-white px-4 py-2.5 text-sm text-[#3D2318] outline-none focus:border-[#C95B7D]"
                    />
                    <button
                        type="button"
                        onClick={() => save({ admin_note: note })}
                        disabled={saving || note === (order.admin_note || "")}
                        data-testid="admin-order-save-note"
                        className="self-end inline-flex items-center gap-1.5 rounded-full bg-[#3D2318] px-4 py-2.5 text-xs font-semibold text-[#F8D7E3] hover:bg-[#C95B7D] hover:text-white disabled:opacity-40 transition-colors"
                    >
                        <Save className="h-3.5 w-3.5" /> Save
                    </button>
                </div>
            </div>
        </article>
    );
}
